import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { ActionModal, CustomToast } from 'components/ui';
import { BaseButton } from 'components/ui/buttons';
import { InputField, RadioField } from 'components/ui/form-fields';
import { AlertIcon, InfoIcon } from 'assets/icons';
import { MediaModal } from 'components/ui/modal/MediaModal';
import { useAssessment } from 'contexts/AssessmentContextProvider';
import { QuestionInfo } from 'components/assessment/common/QuestionInfo';
import {
  AnswerType,
  AssessmentMediaType,
  AssessmentResultAnswerType,
  AssessmentType,
  QuestionType,
  TemperatureType,
} from 'types/assessment';
import { AssessmentResultRecord } from 'record/assessment-result.record';
import { CommonQuestionSteps } from 'components/assessment/CommonQuestionnaire';
import { MessageModal } from 'components/ui/modal/MessageModal';
import parse from 'html-react-parser';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';
import { celsiusToFahrenheit, fahrenheitToCelsius } from 'helpers/temperature-conversion';
import classNames from 'classnames';

const ANSWER_TYPE_FOR_INPUT = ['int', 'float'];
// const RESPIRATORY_RATE_QUESTION_ID = 'b46de6b1-aafa-421f-9886-acba2337329f';
const TEMPERATURE_SYMPTOM_ID = '4d4efb53-2310-46aa-896c-fa5719d5482e';
const MAX_F_TEMPERATURE = 103.1;

enum AssessmentMedia {
  image = 'image',
  video = 'video',
}

type MainAssessmentQuestionnaireProps = {
  relativeStep: number;
  proceedToNextStep: () => void;
};

export const MainAssessmentQuestionnaire = React.forwardRef<unknown, MainAssessmentQuestionnaireProps>(
  ({ relativeStep, proceedToNextStep }, ref) => {
    const [isShownAnnotationModal, setShownAnnotationModal] = useState<boolean>(false);
    const [isShownMediaModal, setShownMediaModal] = useState<boolean>(false);
    const [isShownWarningModal, setShownWarningModal] = useState<boolean>(false);
    const [isSkippedWarning, setSkippedWarning] = useState<boolean>(false);
    const [isAnswerUpdated, setAnswerUpdated] = useState<boolean>(false);

    const [temperatureType, setTemperatureType] = useState<TemperatureType>(TemperatureType.Fahrenheit);
    const [temperature, setTemperature] = useState<string>('');

    const { assessment, assessmentResult, setAssessmentResult, currentStep } = useAssessment();
    const { isOnline } = useNetworkCheck();

    const lengthCommonQuestionnaire = Object.keys(CommonQuestionSteps).length / 2;

    const question = assessment?.questions[relativeStep];

    const handleSkipSeries = useCallback(
      (questionId: string) => {
        if (!assessment) return;

        const nextStep = relativeStep + 1;
        const seriesKey = assessment.questions.find(question => question.id === questionId)?.series_key;
        for (let i = nextStep; i < assessment.questions.length; i++) {
          if (assessment.questions[i].series_key !== seriesKey) {
            return i + lengthCommonQuestionnaire + 1;
          }
        }
        return null;
      },
      [assessment, lengthCommonQuestionnaire, relativeStep],
    );

    const clearSeriesAnswers = useCallback(
      (questionId: string): string[] => {
        if (!assessment) return [];

        const currentQuestion = assessment.questions.find(q => q.id === questionId);
        if (!currentQuestion?.series_key) return [];

        const currentQuestionIndex = assessment.questions.findIndex(q => q.id === questionId);
        const questionIdsToClear: string[] = [];

        // Find all questions in the same series that come after the current question
        for (let i = currentQuestionIndex + 1; i < assessment.questions.length; i++) {
          const question = assessment.questions[i];
          if (question.series_key === currentQuestion.series_key) {
            questionIdsToClear.push(question.id);
          } else {
            // We've reached the end of the series
            break;
          }
        }

        return questionIdsToClear;
      },
      [assessment],
    );

    useImperativeHandle(
      ref,
      () => ({
        handleNextStep: () => {
          if (!assessment) return;

          const currentQuestion = assessment.questions[relativeStep];
          const currentAnswer = assessmentResult?.answers.find(answer => answer.question_id === currentQuestion?.id);
          // const isRespiratoryRateQuestion = currentQuestion?.id === RESPIRATORY_RATE_QUESTION_ID;

          if (
            // isRespiratoryRateQuestion &&
            !isSkippedWarning &&
            currentAnswer &&
            currentAnswer.answer_num &&
            currentQuestion &&
            currentQuestion.upper_threshold &&
            currentQuestion.lower_threshold
          ) {
            if (
              currentAnswer?.answer_num < currentQuestion?.lower_threshold ||
              currentAnswer?.answer_num > currentQuestion?.upper_threshold
            ) {
              setShownWarningModal(true);
              return currentStep;
            }
          }

          if (isSkippedWarning) {
            setSkippedWarning(false);
            setShownWarningModal(false);
          }

          setAnswerUpdated(false);

          const selectedAnswerQuestionOption = currentQuestion?.answer_options.find(
            option => option.id === currentAnswer?.answer_id,
          );

          if (!selectedAnswerQuestionOption?.continue_series && currentQuestion?.series_key) {
            return handleSkipSeries(currentQuestion?.id);
          } else if (relativeStep >= assessment.questions.length - 1) {
            return null;
          } else {
            return currentStep + 1;
          }
        },
      }),
      [assessment, relativeStep, assessmentResult, currentStep, isSkippedWarning, handleSkipSeries],
    );
    useEffect(() => {
      if (isSkippedWarning) {
        proceedToNextStep();
      }
    }, [isSkippedWarning, proceedToNextStep]);

    useEffect(() => {
      setTemperature(v => {
        if (!v) {
          return v;
        }
        const fn = temperatureType === TemperatureType.Celsius ? fahrenheitToCelsius : celsiusToFahrenheit;
        return fn(Number(v)).toString();
      });
    }, [temperatureType, setTemperature]);

    useEffect(() => {
      const currentAnswer = assessmentResult?.answers.find(answer => answer.question_id === question?.id);
      if (isAnswerUpdated && currentAnswer && question?.answer_type === 'options') {
        proceedToNextStep();
      }
    }, [question?.answer_type, question?.id, assessmentResult?.answers, proceedToNextStep, isAnswerUpdated]);

    const updateAnswerBasedOnInput = (event: React.ChangeEvent<HTMLInputElement>, question: QuestionType) => {
      const inputValue = Number(event.target.value);
      const matchingOption = question.answer_options.find(
        option => option.upper_threshold !== undefined && option.upper_threshold >= inputValue,
      );

      if (matchingOption) {
        setAssessmentResult((currentAssessmentResult: AssessmentResultRecord) => {
          const previousAnswers = currentAssessmentResult.answers;
          const existingAnswerIndex = previousAnswers.findIndex(answer => answer.question_id === question.id);
          const newAnswer: AssessmentResultAnswerType = {
            question_id: question.id,
            answer_id: matchingOption.id,
            answer_num: inputValue,
          };

          let newAnswers = [...previousAnswers];
          if (existingAnswerIndex === -1) {
            newAnswers.push(newAnswer);
          } else {
            newAnswers[existingAnswerIndex] = newAnswer;
          }

          // If the matching option has continue_series: false, clear answers from subsequent questions in the series
          if (!matchingOption.continue_series) {
            const questionIdsToClear = clearSeriesAnswers(question.id);
            if (questionIdsToClear.length > 0) {
              newAnswers = newAnswers.filter(answer => !questionIdsToClear.includes(answer.question_id || ''));
            }
          }

          return {
            ...currentAssessmentResult,
            answers: newAnswers,
          };
        });
      }
    };

    const handleAnswer = (currentAnswer: AnswerType, questionId: string) => {
      const selectedAnswer: AssessmentResultAnswerType = { question_id: questionId, answer_id: currentAnswer.id };

      setAssessmentResult((currentAssessmentResult: AssessmentResultRecord) => {
        let answerUpdated = false;

        let newAnswers = currentAssessmentResult.answers.map(answer => {
          if (answer.question_id === questionId) {
            answerUpdated = true;
            return selectedAnswer;
          }
          return answer;
        });

        if (!answerUpdated) {
          newAnswers.push(selectedAnswer);
        }

        // If the selected answer has continue_series: false, clear answers from subsequent questions in the series
        if (!currentAnswer.continue_series) {
          const questionIdsToClear = clearSeriesAnswers(questionId);
          if (questionIdsToClear.length > 0) {
            newAnswers = newAnswers.filter(answer => !questionIdsToClear.includes(answer.question_id || ''));
          }
        }

        return {
          ...currentAssessmentResult,
          answers: newAnswers,
        };
      });
      setAnswerUpdated(true);
    };

    const selectedAnswer = assessmentResult?.answers.find(answer => {
      return answer.question_id === assessment?.questions[relativeStep].id;
    });

    const renderModalWarningDescription = () => {
      const isTemperatureQuestion = question?.symptom_id === TEMPERATURE_SYMPTOM_ID;

      const [lower, upper] = (() => {
        if (
          isTemperatureQuestion &&
          temperatureType === TemperatureType.Celsius &&
          !!question?.lower_threshold &&
          !!question?.upper_threshold
        ) {
          return [fahrenheitToCelsius(question.lower_threshold), fahrenheitToCelsius(question.upper_threshold)];
        }
        return [question?.lower_threshold, question?.upper_threshold];
      })();

      const mainText = isTemperatureQuestion ? 'Normal temperature: ' : 'Normal respiratory rate range: ';

      return parse(`${mainText} <strong>${lower} - ${upper}</strong>`);
    };

    const renderAnswerOption = (answer: AnswerType, questionId: string) => (
      <div
        key={answer.id}
        className={classNames(
          'flex items-center justify-between gap-3 mb-3 self-stretch border rounded-xl dark:hover:bg-white-400/10 dark:hover:border-white-400/20',
          !!selectedAnswer && selectedAnswer.answer_id === answer.id
            ? 'border-gray-800 bg-white-400 dark:bg-white-400/20 dark:border-white-400/20'
            : 'bg-white-400/80 dark:bg-gray-900 border-transparent',
        )}
      >
        <RadioField
          containerClassName="w-full pl-5 pr-3 py-[16px] md:py-[22px]"
          label={answer.text}
          name=""
          value={selectedAnswer?.answer_id}
          checked={!!selectedAnswer && selectedAnswer.answer_id === answer.id}
          onChange={() => handleAnswer(answer, questionId)}
        />
      </div>
    );

    const renderTemperatureQuestion = (question: QuestionType) => {
      const showTemperatureWarning = (selectedAnswer?.answer_num || 0) > MAX_F_TEMPERATURE;
      const maxValue = 999;

      const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setTemperature(
          Number.isNaN(inputValue)
            ? ''
            : Number(inputValue) <=
              (temperatureType === TemperatureType.Celsius ? fahrenheitToCelsius(maxValue) : maxValue)
            ? inputValue
            : temperatureType === TemperatureType.Celsius
            ? fahrenheitToCelsius(maxValue).toString()
            : `${maxValue}`,
        );

        const convertedValue =
          temperatureType === TemperatureType.Celsius && inputValue
            ? celsiusToFahrenheit(Number(inputValue)).toString()
            : inputValue;

        e.target.value = convertedValue;
        updateAnswerBasedOnInput(e, question);
      };

      return (
        <div key={question.id}>
          <div className="flex flex-col gap-2">
            {ANSWER_TYPE_FOR_INPUT.includes(question.answer_type) ? (
              <InputField
                className="px-6 py-[18px] rounded-xl h-16"
                type="number"
                value={temperature}
                min={
                  temperatureType === TemperatureType.Celsius && question.lower_threshold
                    ? fahrenheitToCelsius(question.lower_threshold)
                    : question.lower_threshold
                }
                onKeyDown={e => (e.key === '-' || e.key === 'e') && e.preventDefault()}
                max={
                  temperatureType === TemperatureType.Celsius && question.upper_threshold
                    ? fahrenheitToCelsius(question.upper_threshold)
                    : question.upper_threshold
                }
                onChange={onChange}
              />
            ) : (
              question.answer_options.map(answer => renderAnswerOption(answer, question.id))
            )}
            {showTemperatureWarning && (
              <div className="border p-3 border-red-200 dark:bg-red-600 dark:border-red-500  bg-red-100 rounded-lg flex items-center ">
                <CustomToast
                  notification={{
                    type: 'error',
                    title: `Extremely high temperature detected (> ${
                      temperatureType === TemperatureType.Celsius
                        ? fahrenheitToCelsius(MAX_F_TEMPERATURE)
                        : MAX_F_TEMPERATURE
                    }°${TemperatureType.Celsius === temperatureType ? 'C' : 'F'})`,
                    description: 'Please confirm the value before proceeding',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderQuestion = (question: QuestionType) => {
      return (
        <div key={question.id}>
          <div className="flex flex-col gap-2">
            {ANSWER_TYPE_FOR_INPUT.includes(question.answer_type) ? (
              <InputField
                className="px-6 py-[18px] rounded-xl h-16"
                type="number"
                value={selectedAnswer?.answer_num}
                min={question.lower_threshold}
                onKeyDown={e => (e.key === '-' || e.key === 'e') && e.preventDefault()}
                max={question.upper_threshold}
                onChange={e => updateAnswerBasedOnInput(e, question)}
              />
            ) : (
              question.answer_options.map(answer => renderAnswerOption(answer, question.id))
            )}
          </div>
        </div>
      );
    };

    const renderMedia = (media: AssessmentMediaType) => {
      return (
        <>
          {media.type === AssessmentMedia.image && (
            <div className="my-8">
              <img
                src={`/images/${media.asset}`}
                alt="media"
                className="w-80 h-80 shrink-0 rounded-xl cursor-pointer"
                onClick={() => setShownMediaModal(true)}
              />
            </div>
          )}

          {media.type === AssessmentMedia.video && (
            <div className="my-8">
              <video className="max-w-[420px] h-60 shrink-0 rounded-xl" controls>
                <source src={`/videos/${media.asset}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          )}
        </>
      );
    };

    const onTemperatureTypeChange = useCallback((temp: TemperatureType) => {
      setTemperatureType(temp);
    }, []);

    const renderAssessmentQuestion = (assessment: AssessmentType) => {
      const isTemperatureQuestion = assessment.questions[relativeStep].symptom_id === TEMPERATURE_SYMPTOM_ID;
      return (
        <div>
          <QuestionInfo
            title={assessment.questions[relativeStep].text}
            description={assessment.questions[relativeStep].subtext}
            temperature={
              isTemperatureQuestion
                ? {
                    type: temperatureType,
                    onTypeChange: onTemperatureTypeChange,
                  }
                : undefined
            }
          />

          {assessment.questions.length ? (
            isTemperatureQuestion ? (
              renderTemperatureQuestion(assessment.questions[relativeStep])
            ) : (
              renderQuestion(assessment.questions[relativeStep])
            )
          ) : (
            <div>No Questions</div>
          )}

          {assessment.questions[relativeStep].annotation && (
            <BaseButton
              icon={InfoIcon}
              isIconOnLeftSide
              scheme="secondary"
              size="sm"
              className="!bg-transparent mt-6"
              onClick={() => setShownAnnotationModal(true)}
            >
              {assessment.questions[relativeStep].annotation?.title}
            </BaseButton>
          )}

          {isShownWarningModal && (
            <MessageModal
              icon={AlertIcon}
              iconProps={{ className: 'text-silver-400' }}
              successText="Continue"
              cancelText="Change"
              title={`This value is ${
                selectedAnswer?.answer_num &&
                question?.upper_threshold &&
                selectedAnswer?.answer_num > question?.upper_threshold
                  ? 'above'
                  : 'below'
              } the expected range`}
              description={renderModalWarningDescription()}
              onCancelClick={() => setShownWarningModal(false)}
              onSuccessClick={() => {
                setSkippedWarning(true);
              }}
            />
          )}

          {isOnline &&
            assessment.questions[relativeStep].media &&
            renderMedia(assessment.questions[relativeStep].media as AssessmentMediaType)}

          {isShownMediaModal && (
            <MediaModal
              isVideo={assessment.questions[relativeStep].media?.type === AssessmentMedia.video}
              src={assessment.questions[relativeStep].media?.asset || ''}
              onCancelClick={() => setShownMediaModal(false)}
            />
          )}

          {isShownAnnotationModal && (
            <ActionModal
              onCancelClick={() => setShownAnnotationModal(false)}
              onSuccessClick={() => setShownAnnotationModal(false)}
              iconTitle={InfoIcon}
              iconTitleProps={{ className: '[&>path]:fill-sea-serpent' }}
              successText="Got it!"
              title={assessment.questions[relativeStep].annotation!.title}
              description={assessment.questions[relativeStep].annotation!.text}
            />
          )}
        </div>
      );
    };

    return <>{assessment && <>{renderAssessmentQuestion(assessment)}</>}</>;
  },
);
